import { authenticatedFetch } from "./apiClient";

const API_BASE_URL = import.meta.env.VITE_API_URL;
const LIBRARY_BASE_PATH = "/v1/organizations";

// Re-exported so callers have one import for the library API surface; the
// definitions live apart from this module because they need no auth.
export {
  ACCEPTED_EXTENSIONS,
  ACCEPT_ATTRIBUTE,
  contentTypeForFilename,
  MAX_ORG_LIBRARY_BYTES,
  MAX_UPLOAD_BYTES,
} from "./libraryFormats";

export interface LibraryUploadTicket {
  doc_id: string;
  gcs_path: string;
  upload_url: string;
  expires_in_seconds: number;
}

export interface LibraryDownload {
  download_url: string;
  filename: string;
  expires_in_seconds: number;
}

async function throwIfNotOk(response: Response, action: string): Promise<void> {
  if (response.ok) return;
  let detail = response.statusText;
  try {
    const body: unknown = await response.json();
    if (
      typeof body === "object" &&
      body !== null &&
      typeof (body as { detail?: unknown }).detail === "string"
    ) {
      detail = (body as { detail: string }).detail;
    }
  } catch {
    // Non-JSON error body; statusText is good enough.
  }
  throw new Error(`${action} failed: ${detail}`);
}

export class LibraryService {
  /**
   * Reserve a document and get a signed PUT URL for its bytes.
   *
   * There is deliberately no list method: the collection is read
   * direct-from-Firestore (ADR-0003), so a listing endpoint would be a second
   * way to answer the same question.
   */
  static async createUpload(
    orgId: string,
    filename: string,
    contentType: string,
  ): Promise<LibraryUploadTicket> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${LIBRARY_BASE_PATH}/${orgId}/library`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filename, content_type: contentType }),
      },
    );
    await throwIfNotOk(response, "Preparing the upload");
    return response.json();
  }

  /**
   * PUT the bytes straight to GCS, reporting progress.
   *
   * `XMLHttpRequest` rather than `fetch` purely for `upload.onprogress` — a
   * 10MB file on a slow connection is long enough that a silent bar is worse
   * than no bar. The `Content-Type` must match what was signed exactly, or GCS
   * rejects the signature.
   *
   * This request goes to `storage.googleapis.com`, so the bucket needs a CORS
   * configuration naming this origin. Without it the preflight fails and
   * nothing here runs — and `curl` cannot reproduce that.
   */
  static uploadToSignedUrl(
    uploadUrl: string,
    file: File,
    contentType: string,
    onProgress?: (fraction: number) => void,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open("PUT", uploadUrl, true);
      request.setRequestHeader("Content-Type", contentType);
      request.upload.onprogress = (event) => {
        if (event.lengthComputable && onProgress) {
          onProgress(event.loaded / event.total);
        }
      };
      request.onload = () => {
        if (request.status >= 200 && request.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed (${request.status})`));
        }
      };
      request.onerror = () =>
        reject(
          new Error(
            "Upload failed. If this persists, the storage bucket may be missing its CORS configuration.",
          ),
        );
      request.send(file);
    });
  }

  /** Confirm the bytes landed. Core stats the object and enforces both caps here. */
  static async finalizeUpload(orgId: string, docId: string): Promise<void> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${LIBRARY_BASE_PATH}/${orgId}/library/${docId}/finalize`,
      { method: "POST" },
    );
    await throwIfNotOk(response, "Finishing the upload");
  }

  static async getDownloadUrl(
    orgId: string,
    docId: string,
  ): Promise<LibraryDownload> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${LIBRARY_BASE_PATH}/${orgId}/library/${docId}/download`,
      { method: "GET" },
    );
    await throwIfNotOk(response, "Preparing the download");
    return response.json();
  }

  static async deleteDocument(orgId: string, docId: string): Promise<void> {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${LIBRARY_BASE_PATH}/${orgId}/library/${docId}`,
      { method: "DELETE" },
    );
    await throwIfNotOk(response, "Deleting the document");
  }
}
